import moment from 'moment';
import React, { useEffect, useRef } from 'react';
import { 
  RiDeleteBin6Line, 
  RiEdit2Line, 
  RiEyeLine,
  RiSearchLine,
  RiFilterLine,
  RiMore2Line,
  RiRefreshLine
} from 'react-icons/ri';
import { 
  FiSearch, 
  FiFilter, 
  FiEye, 
  FiEdit2, 
  FiTrash2, 
  FiUser, 
  FiDollarSign, 
  FiClock, 
  FiTag, 
  FiRefreshCw, 
  FiMoreVertical,
  FiCalendar,
  FiPercent,
  FiFileText,
  FiCreditCard,
  FiArrowUp,
  FiArrowDown,
  FiTrendingUp,
  FiTrendingDown,
  FiCheckCircle,
  FiXCircle
} from "react-icons/fi";
import { 
  MdOutlineSearch, 
  MdOutlineFilter, 
  MdOutlineVisibility,
  MdOutlineEdit,
  MdOutlineDelete,
  MdOutlinePerson,
  MdOutlineAttachMoney,
  MdOutlineCalendarToday,
  MdOutlineAccessTime,
  MdOutlineReceipt,
  MdOutlineFileCopy,
  MdOutlineCreditCard
} from "react-icons/md";
import DatePicker from '../../../components/ui/FromDateToDate';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import ReactPaginate from 'react-paginate';

const ViewSubTable = ({
  TableName,
  TableHeader,
  TableData,
  showMenu,
  deleteAction,
  handleToggle,
  setFilter,
  filter,
  handleCreateModal,
  keyword,
  setKeyword,
  pageNo,
  viewAction,
  setpageNo,
  size,
  setSize, 
  total, 
  handlePageClick
}) => {
  const ContentActionRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ContentActionRef.current && !ContentActionRef.current.contains(event.target)) {
        showMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleDateChange = (name, value) => {
    setFilter((p) => ({
      ...p,
      [name]: value
    }));
  };

  // Transaction type badge
  const TypeBadge = ({ type }) => {
    const typeConfig = {
      'credit': { icon: FiTrendingUp, color: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: 'Credit' },
      'debit': { icon: FiTrendingDown, color: 'text-rose-700 bg-rose-50 border-rose-200', label: 'Debit' },
      'withdrawal': { icon: FiArrowUp, color: 'text-amber-700 bg-amber-50 border-amber-200', label: 'Withdrawal' },
      'deposit': { icon: FiArrowDown, color: 'text-blue-700 bg-blue-50 border-blue-200', label: 'Deposit' }
    };
    const config = typeConfig[type?.toLowerCase()] || { icon: FiTag, color: 'text-gray-700 bg-gray-50 border-gray-200', label: type || 'N/A' };
    const Icon = config.icon;
    
    return (
      <span className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
        ${config.color}
      `}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="w-full">
      <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100/80 overflow-hidden">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 md:p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-lg">
              <FiFileText className="w-4 h-4 text-[#715EFE]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-800">{TableName}</h2>
              <p className="text-xs text-gray-400 hidden sm:block">
                {TableData?.length || 0} records
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            {/* Requested Date */}
            <div className="relative">
              <DatePicker
                date={filter.fromDate}
                label={
                  <div className="flex items-center gap-1.5">
                    <FiCalendar className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs">Requested</span>
                  </div>
                }
                onDateChange={(value) => handleDateChange("fromDate", value)}
                className="w-[140px]"
              />
            </div>

            {/* Confirm Date */}
            <div className="relative">
              <DatePicker
                date={filter.toDate}
                label={
                  <div className="flex items-center gap-1.5">
                    <FiCheckCircle className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs">Confirmed</span>
                  </div>
                }
                onDateChange={(value) => handleDateChange("toDate", value)}
                className="w-[140px]"
              />
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50/80 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                {TableHeader.map((heading, index) => {
                  const headerIcons = {
                    '#': null,
                    'User': FiUser,
                    'Amount': FiDollarSign,
                    'Admin Fee': FiPercent,
                    'Transfer Id': FiFileText,
                    'Type': FiTag,
                    'Release Date': FiCalendar,
                    'View Transactions': FiEye
                  };
                  const Icon = headerIcons[heading];
                  return (
                    <th key={index} className="px-3 md:px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {Icon && <Icon className="w-3.5 h-3.5 text-gray-400" />}
                        <span>{heading}</span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {TableData && TableData.length > 0 ? (
                TableData.map((ele, index) => (
                  <tr key={index} className="hover:bg-gray-50/70 transition-colors duration-150 group">
                    <td className="px-3 md:px-4 py-3">
                      <span className="text-sm font-medium text-gray-400">
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center">
                          <span className="text-xs font-bold text-[#715EFE]">
                            {ele?.userInfo?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-gray-800 truncate max-w-[120px]">
                          {ele?.userInfo?.fullName || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <span className="text-sm font-semibold text-emerald-600">
                        ${ele?.expertAmount?.toFixed(2) || '0.00'}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <span className="text-sm font-medium text-blue-600">
                        ${ele?.adminFee?.toFixed(2) || '0.00'}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center">
                          <FiFileText className="w-3.5 h-3.5 text-[#715EFE]" />
                        </div>
                        <span className="text-xs font-mono text-gray-600">
                          {ele?.transferId || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <TypeBadge type={ele?.type} />
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-700 whitespace-nowrap">
                          {ele?.createdAt ? moment(ele.createdAt).format('MMM D, YYYY') : '-'}
                        </span>
                        {ele?.createdAt && (
                          <span className="text-xs text-gray-400">
                            {moment(ele.createdAt).format('h:mm A')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <button
                        className="p-1.5 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                        onClick={() => viewAction(ele)}
                        title="View Details"
                      >
                        <FiEye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={TableHeader.length} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-4 bg-gray-50 rounded-full">
                        <FiFileText className="w-10 h-10 text-gray-300" />
                      </div>
                      <p className="text-sm font-medium text-gray-500">No records found</p>
                      <p className="text-xs text-gray-400">Try adjusting your date filters</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile View for Small Screens */}
        <div className="md:hidden divide-y divide-gray-100">
          {TableData && TableData.length > 0 ? (
            TableData.map((ele, index) => (
              <div key={index} className="p-4 hover:bg-gray-50/70 transition-colors duration-150">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-400">
                      #{index + 1}
                    </span>
                    <TypeBadge type={ele?.type} />
                  </div>
                  <button
                    className="p-1.5 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    onClick={() => viewAction(ele)}
                  >
                    <FiEye className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">User</span>
                    <span className="text-sm font-semibold text-gray-800">
                      {ele?.userInfo?.fullName || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Amount</span>
                    <span className="text-sm font-semibold text-emerald-600">
                      ${ele?.expertAmount?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Admin Fee</span>
                    <span className="text-sm font-medium text-blue-600">
                      ${ele?.adminFee?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Transfer ID</span>
                    <span className="text-xs font-mono text-gray-600">
                      {ele?.transferId || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Date</span>
                    <span className="text-sm text-gray-700">
                      {ele?.createdAt ? moment(ele.createdAt).format('MMM D, YYYY') : '-'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center">
              <div className="flex flex-col items-center gap-2">
                <FiFileText className="w-10 h-10 text-gray-300" />
                <p className="text-sm text-gray-500">No records found</p>
              </div>
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > size && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <div className="text-sm text-gray-500">
              Showing {((pageNo - 1) * size) + 1} to {Math.min(pageNo * size, total)} of {total} entries
            </div>
            <ReactPaginate
              previousLabel={'Prev'}
              nextLabel={'Next'}
              breakLabel={'...'}
              breakClassName={'px-2 text-gray-400'}
              pageCount={Math.ceil(total / size)}
              marginPagesDisplayed={1}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={'flex items-center gap-1'}
              pageClassName={'px-2'}
              pageLinkClassName={'px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200'}
              previousClassName={'px-2'}
              previousLinkClassName={'px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200'}
              nextClassName={'px-2'}
              nextLinkClassName={'px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200'}
              activeClassName={'bg-[#715EFE] text-white rounded-md'}
              activeLinkClassName={'bg-[#715EFE] text-white px-3 py-1 rounded-md'}
              disabledClassName={'opacity-50 cursor-not-allowed'}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewSubTable;